import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSocial } from "../../contexts/SocialContext";
import { CLASSES } from "../../data/classes";
import { BORDERS_BY_ID } from "../../data/cosmetics";
import { RotateCcw, Trophy, Swords, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEDAL = ["🥇", "🥈", "🥉"];

const METRICS = [
  { id: "runs", label: "Runs", icon: RotateCcw, suffix: "", color: "text-sky-400" },
  { id: "floor", label: "Étage max", icon: Trophy, suffix: "", color: "text-yellow-400" },
  { id: "damage", label: "Dégâts max", icon: Swords, suffix: "", color: "text-rose-400" },
];

export const StatsLeaderboard = ({ limit = 5 }) => {
  const { openProfile } = useSocial();
  const [metric, setMetric] = useState("runs");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leaderboard/stats?metric=${m}&limit=${limit}`);
      setEntries(res.data.entries || []);
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { load(metric); }, [metric, load]);
  // Light real-time refresh
  useEffect(() => {
    const t = setInterval(() => load(metric), 15000);
    return () => clearInterval(t);
  }, [metric, load]);

  const cur = METRICS.find((m) => m.id === metric);

  return (
    <div data-testid="stats-leaderboard" className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-4 cartoon-shadow-lg-static w-full">
      <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2 text-emerald-400">
        <Trophy className="w-5 h-5" /> Records mondiaux
      </h2>
      <div className="flex gap-1.5 mb-3">
        {METRICS.map((m) => {
          const Icon = m.icon;
          const active = m.id === metric;
          return (
            <button key={m.id} data-testid={`stats-tab-${m.id}`} onClick={() => setMetric(m.id)}
              className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-xl border-2 border-slate-950 text-xs font-display font-bold uppercase tracking-wide transition-colors ${active ? "bg-emerald-500 text-slate-950" : "bg-slate-900 text-slate-300"}`}>
              <Icon className="w-3.5 h-3.5" /> {m.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400 gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>
      ) : entries.length === 0 ? (
        <p data-testid="stats-leaderboard-empty" className="text-slate-400 text-sm italic text-center py-4">Aucun record pour l'instant. Lance une run !</p>
      ) : (
        <ol className="space-y-1.5">
          {entries.map((e) => {
            const cls = e.avatar_classe ? CLASSES[e.avatar_classe] : null;
            const border = BORDERS_BY_ID[e.equipped_border || "default"] || BORDERS_BY_ID.default;
            return (
              <li key={e.user_id}>
                <button
                  data-testid={`stats-entry-${e.rank}`}
                  onClick={() => openProfile(e.user_id)}
                  className={`w-full flex items-center gap-2 ${e.rank <= 3 ? "bg-slate-900" : "bg-slate-900/60"} border-2 border-slate-950 rounded-xl p-2 hover:border-emerald-500 transition-colors text-left`}
                >
                  <span className="w-8 text-center font-display font-bold text-emerald-400">{MEDAL[e.rank - 1] || `#${e.rank}`}</span>
                  <div className={`w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 ${border.style.className}`}>
                    {cls ? (
                      <div className={`w-full h-full bg-gradient-to-br ${cls.couleur}`}><img src={cls.image} alt="" className="w-full h-full object-cover" /></div>
                    ) : e.picture ? (
                      <img src={e.picture} alt="" className="w-full h-full object-cover" />
                    ) : (<div className="w-full h-full bg-slate-700" />)}
                  </div>
                  <p className="flex-1 min-w-0 font-display font-bold text-sm text-white truncate">{e.name}</p>
                  <span className={`font-display font-bold text-sm ${cur.color}`}>{e.value}{cur.suffix}</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
